import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MdAdd,
  MdCalendarToday,
  MdChevronLeft,
  MdChevronRight,
  MdClose,
  MdContentCopy,
  MdDeleteOutline,
  MdEdit,
  MdEventAvailable,
  MdLocationOn,
  MdSchedule,
} from 'react-icons/md';
import DashboardLayout from '../../components/DashboardLayout';
import reminderService from '../../services/reminderService';
import todoService from '../../services/todoService';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const VIEW_OPTIONS = [
  { label: 'Day', value: 'day' },
  { label: 'Week', value: 'week' },
  { label: 'Month', value: 'month' },
];

const REPEAT_OPTIONS = [
  { label: 'Does not repeat', value: 'none' },
  { label: 'Every day', value: 'daily' },
  { label: 'Every week', value: 'weekly' },
  { label: 'Every month', value: 'monthly' },
];

const QUICK_COLOR_SWATCHES = [
  '#4f46e5',
  '#0ea5e9',
  '#10b981',
  '#f59e0b',
  '#fb7185',
  '#6d28d9',
  '#2563eb',
  '#f43f5e',
  '#a855f7',
  '#14b8a6',
  '#9333ea',
  '#ef4444',
  '#2dd4bf',
  '#f97316',
  '#15803d',
];


const FALLBACK_LABEL_TOKEN = {
  label: 'General',
  backgroundColor: '#eef2ff',
  borderColor: '#c7d2fe',
  textColor: '#4338ca',
  dotColor: '#4f46e5',
};

const TODO_LABEL_TOKEN = {
  label: 'Task',
  backgroundColor: '#f0fdf4',
  borderColor: '#bbf7d0',
  textColor: '#166534',
  dotColor: '#22c55e',
};

const sanitizeColor = (value, fallback) => {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  if (/^(#|rgb|hsl)/i.test(trimmed)) {
    return trimmed;
  }
  return `#${trimmed.replace(/^#/, '')}`;
};

const buildTokenFromApiLabel = (label) => {
  const palette = label?.colors || {};
  const background = label?.backgroundColor || label?.bgColor || palette.background || palette.bg;
  const border = label?.borderColor || label?.border || palette.border;
  const text = label?.textColor || label?.text || palette.text || palette.color;
  const dot = label?.dotColor || label?.dot || label?.accentColor || label?.color || palette.dot;

  return {
    label: label?.label || label?.name || label?.title || 'Label',
    backgroundColor: sanitizeColor(background, FALLBACK_LABEL_TOKEN.backgroundColor),
    borderColor: sanitizeColor(border, FALLBACK_LABEL_TOKEN.borderColor),
    textColor: sanitizeColor(text, FALLBACK_LABEL_TOKEN.textColor),
    dotColor: sanitizeColor(dot, FALLBACK_LABEL_TOKEN.dotColor),
  };
};

const getChipStyle = (token) => ({
  borderColor: token.borderColor,
  backgroundColor: token.backgroundColor,
  color: token.textColor,
});

const getSurfaceStyle = (token) => ({
  borderColor: token.borderColor,
  backgroundColor: token.backgroundColor,
});

const getDotStyle = (token) => ({ backgroundColor: token.dotColor });

const deriveLabelKey = (label) => {
  const raw = label?.slug || label?.key || label?.code || label?.id || label?.name;
  if (!raw) return null;
  return String(raw).trim().toLowerCase().replace(/\s+/g, '-');
};

const clampToMidnight = (value) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const pad = (input) => String(input).padStart(2, '0');

const toDateKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const toDateFromKey = (key) => {
  const [year, month, day] = key.split('-').map(Number);
  return clampToMidnight(new Date(year, month - 1, day));
};

const buildDateKeyRange = (startDate, endDate) => {
  const cursor = clampToMidnight(new Date(startDate));
  const limit = clampToMidnight(new Date(endDate));
  const keys = [];
  while (cursor.getTime() <= limit.getTime()) {
    keys.push(toDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
};

const buildReminderDraft = (overrides = {}) => ({
  title: '',
  date: toDateKey(new Date()),
  time: '09:00',
  durationMins: 45,
  location: '',
  description: '',
  color: 'indigo',
  repeat: 'none',
  repeatCount: 1,
  ...overrides,
});

const buildTodoDraft = (overrides = {}) => ({
  task_name: '',
  description: '',
  due_date: toDateKey(new Date()),
  status: 'pending',
  ...overrides,
});

const slugifyLabel = (input) =>
  input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const normalizeHexColor = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (!match) return null;
  let hex = match[1];
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }
  return `#${hex.toLowerCase()}`;
};

const isLocationLink = (value) => typeof value === 'string' && /(https?:\/\/|www\.)/i.test(value.trim());

const toTodoDateKey = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return toDateKey(clampToMidnight(date));
};

const hexToRgb = (hex) => {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return null;
  const value = parseInt(normalized.slice(1), 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
};

const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b]
    .map((component) => Math.max(0, Math.min(255, component)).toString(16).padStart(2, '0'))
    .join('')}`;

const blendWithWhite = (hex, ratio = 0.5) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return FALLBACK_LABEL_TOKEN.backgroundColor;
  const mix = (component) => Math.round(component + (255 - component) * ratio);
  return rgbToHex({ r: mix(rgb.r), g: mix(rgb.g), b: mix(rgb.b) });
};

const buildCustomTokenFromHex = (labelName, hex) => {
  const normalized = normalizeHexColor(hex);
  if (!normalized) {
    return { ...FALLBACK_LABEL_TOKEN, label: labelName };
  }
  return {
    label: labelName,
    backgroundColor: blendWithWhite(normalized, 0.9),
    borderColor: blendWithWhite(normalized, 0.6),
    textColor: '#1f2937',
    dotColor: normalized,
  };
};

const toDateWithTime = (dateKey, time) => {
  const base = toDateFromKey(dateKey);
  const safeTime = typeof time === 'string' && time.includes(':') ? time : '00:00';
  const [hours = 0, minutes = 0] = safeTime.split(':').map(Number);
  base.setHours(hours, minutes, 0, 0);
  return base;
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const isSameMonth = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

const formatMonthLabel = (date) =>
  date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

const formatFullDate = (date) =>
  date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

const formatShortDate = (date) =>
  date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

const formatTimeLabel = (time) => {
  if (!time) return 'All day';
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  const date = new Date(2024, 0, 1, hours, minutes);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const formatRangeLabel = (time, durationMins = 45) => {
  if (!time) return 'All day';
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  const start = new Date(2024, 0, 1, hours, minutes);
  const end = new Date(start);
  end.setMinutes(end.getMinutes() + durationMins);
  const startLabel = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const endLabel = end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${startLabel} – ${endLabel}`;
};

const getWeekStart = (date) => {
  const start = clampToMidnight(date);
  start.setDate(start.getDate() - start.getDay());
  return start;
};

const buildWeekDays = (anchor) => {
  const start = getWeekStart(anchor);
  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return clampToMidnight(day);
  });
};

const buildMonthGrid = (anchor) => {
  const firstDay = clampToMidnight(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  const start = getWeekStart(firstDay);
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return clampToMidnight(day);
  });
};

const sortByDateTime = (list) =>
  [...list].sort(
    (a, b) => toDateWithTime(a.date, a.time).getTime() - toDateWithTime(b.date, b.time).getTime(),
  );

const shiftDateByFrequency = (anchorDate, frequency, offsetSteps) => {
  const base = new Date(anchorDate);
  if (frequency === 'daily') {
    base.setDate(base.getDate() + offsetSteps);
  } else if (frequency === 'weekly') {
    base.setDate(base.getDate() + offsetSteps * 7);
  } else if (frequency === 'monthly') {
    base.setMonth(base.getMonth() + offsetSteps);
  }
  return clampToMidnight(base);
};

const expandReminderOccurrences = (reminder) => {
  const frequency = reminder.repeat || 'none';
  const repeats = Number(reminder.repeatCount) || 1;
  const totalOccurrences = frequency === 'none' ? 1 : Math.max(1, repeats);
  const startDate = toDateFromKey(reminder.date);
  return Array.from({ length: totalOccurrences }, (_, index) => {
    const occurrenceDate = shiftDateByFrequency(startDate, frequency, index);
    return {
      ...reminder,
      occurrenceIndex: index,
      occurrenceId: `${reminder.id}-${index}`,
      date: toDateKey(occurrenceDate),
    };
  });
};

const normalizeReminderFromApi = (reminder) => {
  if (!reminder) return null;
  return {
    id: reminder.id || Date.now(),
    title: reminder.title || 'Reminder',
    date: reminder.date || toDateKey(new Date()),
    time: reminder.time || null,
    durationMins: Number(reminder.durationMins) || 45,
    location: reminder.location || '',
    description: reminder.description || '',
    color: reminder.color || 'default',
    repeat: reminder.repeat || 'none',
    repeatCount: Number(reminder.repeatCount) || 1,
  };
};

const normalizeRemindersList = (list) =>
  (Array.isArray(list) ? list : [])
    .map((item) => normalizeReminderFromApi(item))
    .filter(Boolean);

const groupRemindersByDate = (items) => {
  const grouped = items.reduce((acc, reminder) => {
    const key = reminder.date;
    if (!acc[key]) acc[key] = [];
    acc[key].push(reminder);
    return acc;
  }, {});
  Object.keys(grouped).forEach((key) => {
    grouped[key] = sortByDateTime(grouped[key]);
  });
  return grouped;
};

export default function Remider() {
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(() => clampToMidnight(new Date()));
  const [selectedDate, setSelectedDate] = useState(() => clampToMidnight(new Date()));
  const [reminders, setReminders] = useState([]);
  const [newReminder, setNewReminder] = useState(() => buildReminderDraft());
  const [editingReminderId, setEditingReminderId] = useState(null);
  const [labelTokens, setLabelTokens] = useState({ default: FALLBACK_LABEL_TOKEN });
  const resolveLabelToken = (key) => labelTokens[key] || labelTokens.default;
  const [formOpen, setFormOpen] = useState(false);
  const [listModalOpen, setListModalOpen] = useState(false);
  const [labelFilter, setLabelFilter] = useState('all');
  const [showAddLabelForm, setShowAddLabelForm] = useState(false);
  const [showEditLabelForm, setShowEditLabelForm] = useState(false);
  const [editingLabelKey, setEditingLabelKey] = useState('');
  const [newLabelDraft, setNewLabelDraft] = useState({ name: '', color: '#4f46e5' });
  const [editLabelDraft, setEditLabelDraft] = useState({ name: '', color: '#4f46e5' });
  const [labelCreationError, setLabelCreationError] = useState('');
  const [labelEditError, setLabelEditError] = useState('');
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState('');
  const [labelSaving, setLabelSaving] = useState(false);
  const [labelEditSaving, setLabelEditSaving] = useState(false);
  const [reminderSaving, setReminderSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deletingReminderIds, setDeletingReminderIds] = useState([]);
  const [deleteError, setDeleteError] = useState('');
  const [pendingDeleteReminder, setPendingDeleteReminder] = useState(null);
  const [copiedLocationId, setCopiedLocationId] = useState(null);
  const copyResetRef = useRef(null);
  const [todos, setTodos] = useState([]);
  const [todoLoading, setTodoLoading] = useState(true);
  const [todoError, setTodoError] = useState('');
  const [todoActionError, setTodoActionError] = useState('');
  const [todoUpdatingIds, setTodoUpdatingIds] = useState([]);
  const [todoDeletingIds, setTodoDeletingIds] = useState([]);
  const [pendingDeleteTodo, setPendingDeleteTodo] = useState(null);
  const [showAddTodoModal, setShowAddTodoModal] = useState(false);
  const [newTodoForm, setNewTodoForm] = useState(() => buildTodoDraft());
  const [editingTodoId, setEditingTodoId] = useState(null);
  const [todoSaving, setTodoSaving] = useState(false);
  const [todoFormError, setTodoFormError] = useState('');
  const refreshReminders = useCallback(async () => {
    try {
      const response = await reminderService.fetchReminders();
      const normalizedList = normalizeRemindersList(response);
      setReminders(sortByDateTime(normalizedList));
      return true;
    } catch (err) {
      console.error('Failed to refresh reminders', err);
      return false;
    }
  }, [setReminders]);

  const refreshTodos = useCallback(async () => {
    setTodoLoading(true);
    setTodoError('');
    try {
      const response = await todoService.fetchTodos();
      setTodos(Array.isArray(response) ? response : []);
      setTodoError('');
      return true;
    } catch (err) {
      console.error('Failed to load todos', err);
      setTodoError('Unable to load tasks from the server.');
      return false;
    } finally {
      setTodoLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      setDataLoading(true);
      setDataError('');
      try {
        const [labelsResponse, remindersResponse] = await Promise.all([
          reminderService.fetchLabels(),
          reminderService.fetchReminders(),
        ]);

        if (cancelled) {
          return;
        }

        const list = Array.isArray(labelsResponse) ? labelsResponse : [];
        const tokensFromServer = list.reduce(
          (acc, label) => {
            const key = label?.slug || deriveLabelKey(label);
            if (!key) {
              return acc;
            }
            acc[key] = buildTokenFromApiLabel(label);
            return acc;
          },
          { default: FALLBACK_LABEL_TOKEN },
        );

        const nextTokens = tokensFromServer;

        setLabelTokens(nextTokens);
        setNewReminder((prev) => {
          if (prev.color && nextTokens[prev.color]) {
            return prev;
          }
          const fallbackKey = Object.keys(nextTokens).find((key) => key !== 'default');
          return {
            ...prev,
            color: fallbackKey || 'default',
          };
        });

        const normalizedReminders = normalizeRemindersList(remindersResponse);
        setReminders(sortByDateTime(normalizedReminders));
      } catch (err) {
        console.error('Failed to hydrate reminders', err);
        if (!cancelled) {
          setDataError('Unable to load reminders from the server.');
        }
      } finally {
        if (!cancelled) {
          setDataLoading(false);
        }
      }
    };

    hydrate();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    refreshTodos();
  }, [refreshTodos]);

  useEffect(() => () => {
    if (copyResetRef.current) {
      clearTimeout(copyResetRef.current);
    }
  }, []);

  const todayKey = toDateKey(new Date());

  const calendarTitle = useMemo(() => {
    if (view === 'month') {
      return formatMonthLabel(currentDate);
    }
    if (view === 'week') {
      const start = getWeekStart(currentDate);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      const sameYear = start.getFullYear() === end.getFullYear();
      const startLabel = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const endLabel = end.toLocaleDateString('en-US', {
        month: start.getMonth() === end.getMonth() ? undefined : 'short',
        day: 'numeric',
        year: sameYear ? undefined : 'numeric',
      });
      return `${startLabel} – ${endLabel}${sameYear ? `, ${start.getFullYear()}` : ''}`;
    }
    return formatFullDate(selectedDate);
  }, [currentDate, selectedDate, view]);

  const calendarDays = useMemo(() => {
    if (view === 'month') return buildMonthGrid(currentDate);
    if (view === 'week') return buildWeekDays(currentDate);
    return [selectedDate];
  }, [currentDate, selectedDate, view]);

  const miniMonthDays = useMemo(() => buildMonthGrid(currentDate), [currentDate]);

  const expandedReminders = useMemo(
    () => reminders.flatMap((reminder) => expandReminderOccurrences(reminder)),
    [reminders],
  );

  const remindersByDate = useMemo(() => groupRemindersByDate(expandedReminders), [expandedReminders]);

  const todosByDate = useMemo(() => {
    return todos.reduce((acc, todo) => {
      const key = toTodoDateKey(todo?.due_date);
      if (!key) {
        return acc;
      }
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(todo);
      return acc;
    }, {});
  }, [todos]);

  const viewRangeDateKeys = useMemo(() => {
    if (view === 'month') {
      const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      return buildDateKeyRange(monthStart, monthEnd);
    }
    if (view === 'week') {
      return buildWeekDays(currentDate).map((day) => toDateKey(day));
    }
    return [toDateKey(selectedDate)];
  }, [view, currentDate, selectedDate]);

  const viewRangeReminders = useMemo(
    () => viewRangeDateKeys.flatMap((key) => remindersByDate[key] || []),
    [viewRangeDateKeys, remindersByDate],
  );

  const filteredRemindersByDate = useMemo(() => {
    if (labelFilter === 'all') {
      return remindersByDate;
    }
    const filteredList = expandedReminders.filter((item) => item.color === labelFilter);
    return groupRemindersByDate(filteredList);
  }, [labelFilter, expandedReminders, remindersByDate]);

  const selectedDayKey = toDateKey(selectedDate);
  const selectedDayReminders = remindersByDate[selectedDayKey] || [];
  const selectedDayTodos = todosByDate[selectedDayKey] || [];
  const customLabelKeys = useMemo(
    () => Object.keys(labelTokens).filter((key) => key !== 'default'),
    [labelTokens],
  );
  const hasCustomLabels = customLabelKeys.length > 0;
  const isEditingReminder = Boolean(editingReminderId);
  const isEditingTodo = Boolean(editingTodoId);
  const deriveReminderColor = useCallback(
    (preferred) => {
      if (preferred && labelTokens[preferred]) {
        return preferred;
      }
      return customLabelKeys[0] || 'default';
    },
    [labelTokens, customLabelKeys],
  );
  const draftColorHex = normalizeHexColor(newLabelDraft.color);
  const draftPreviewToken = useMemo(() => {
    if (!draftColorHex) return null;
    return buildCustomTokenFromHex(newLabelDraft.name || 'Preview', draftColorHex);
  }, [draftColorHex, newLabelDraft.name]);
  const editDraftColorHex = normalizeHexColor(editLabelDraft.color);
  const editDraftPreviewToken = useMemo(() => {
    if (!editDraftColorHex) return null;
    return buildCustomTokenFromHex(editLabelDraft.name || 'Preview', editDraftColorHex);
  }, [editDraftColorHex, editLabelDraft.name]);
  const labelContextTitle = useMemo(() => {
    if (view === 'month') {
      return formatMonthLabel(currentDate);
    }
    if (view === 'week') {
      const start = getWeekStart(currentDate);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      const sameMonth = start.getMonth() === end.getMonth();
      const sameYear = start.getFullYear() === end.getFullYear();
      const startLabel = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const endLabel = end.toLocaleDateString('en-US', {
        month: sameMonth ? undefined : 'short',
        day: 'numeric',
        year: sameYear ? undefined : 'numeric',
      });
      return `${startLabel} – ${endLabel}${sameYear ? `, ${start.getFullYear()}` : ''}`;
    }
    return formatShortDate(selectedDate);
  }, [view, currentDate, selectedDate]);
  const labelFilterOptions = useMemo(() => {
    const availableKeys = Object.keys(labelTokens).filter((key) => key !== 'default');
    const reminderKeys = viewRangeReminders.map((item) => item.color).filter(Boolean);
    const uniqueKeys = Array.from(new Set([...availableKeys, ...reminderKeys])).filter(Boolean);

    const colorOptions = uniqueKeys.map((colorKey) => {
      const meta = resolveLabelToken(colorKey);
      return {
        value: colorKey,
        label: meta.label,
        chipStyle: getChipStyle(meta),
        dotStyle: getDotStyle(meta),
      };
    });

    return [
      {
        value: 'all',
        label: 'All labels',
        chipStyle: { borderColor: '#e5e7eb', backgroundColor: '#ffffff', color: '#4b5563' },
        dotStyle: { backgroundColor: '#9ca3af' },
      },
      ...colorOptions,
    ];
  }, [labelTokens, viewRangeReminders]);
  const filteredSelectedReminders = useMemo(() => {
    if (labelFilter === 'all') {
      return selectedDayReminders;
    }
    return selectedDayReminders.filter((item) => item.color === labelFilter);
  }, [labelFilter, selectedDayReminders]);
  const activeLabelMeta = labelFilter === 'all' ? null : resolveLabelToken(labelFilter);
  const pendingTodoCount = useMemo(
    () => selectedDayTodos.filter((todo) => todo.status === 'pending').length,
    [selectedDayTodos],
  );
  const completedTodoCount = useMemo(
    () => selectedDayTodos.filter((todo) => todo.status === 'completed').length,
    [selectedDayTodos],
  );
  const pendingDeleteInFlight = pendingDeleteReminder
    ? deletingReminderIds.includes(String(pendingDeleteReminder.id))
    : false;
  const pendingTodoDeleteInFlight = pendingDeleteTodo
    ? todoDeletingIds.includes(String(pendingDeleteTodo.id))
    : false;

  const handleNavigate = (direction) => {
    setCurrentDate((prev) => {
      const next = new Date(prev);
      if (view === 'month') {
        next.setMonth(next.getMonth() + direction);
      } else if (view === 'week') {
        next.setDate(next.getDate() + direction * 7);
      } else {
        next.setDate(next.getDate() + direction);
      }
      return clampToMidnight(next);
    });
  };

  const goToToday = () => {
    const today = clampToMidnight(new Date());
    setCurrentDate(today);
    setSelectedDate(today);
    setNewReminder((prev) => ({ ...prev, date: toDateKey(today) }));
  };

  const handleSelectDay = (day, { openModal = true } = {}) => {
    const normalized = clampToMidnight(day);
    setSelectedDate(normalized);
    if (view !== 'month' || !isSameMonth(normalized, currentDate)) {
      setCurrentDate(normalized);
    }
    setNewReminder((prev) => ({ ...prev, date: toDateKey(normalized) }));
    if (openModal) {
      setListModalOpen(true);
    }
  };

  const handleViewChange = (nextView) => {
    setView(nextView);
    if (nextView === 'day') {
      setSelectedDate(clampToMidnight(currentDate));
    }
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setNewReminder((prev) => ({ ...prev, [name]: value }));
  };

  const handleLabelDraftChange = (event) => {
    const { name, value } = event.target;
    let nextValue = value;
    if (name === 'color') {
      const trimmed = value.trim();
      if (!trimmed) {
        nextValue = '';
      } else {
        const core = trimmed.replace(/^#+/, '');
        nextValue = `#${core}`;
      }
    }
    setNewLabelDraft((prev) => ({ ...prev, [name]: nextValue }));
    if (labelCreationError) {
      setLabelCreationError('');
    }
  };

  const handleOpenCreateReminderForm = () => {
    setFormError('');
    setEditingReminderId(null);
    setNewReminder(
      buildReminderDraft({
        date: toDateKey(selectedDate),
        color: deriveReminderColor(newReminder.color),
      }),
    );
    setFormOpen(true);
  };

  const handleEditReminder = (reminder, options = {}) => {
    if (!reminder) {
      return;
    }
    const targetDate = reminder.date || toDateKey(new Date());
    setFormError('');
    setEditingReminderId(reminder.id);
    setNewReminder(
      buildReminderDraft({
        title: reminder.title || '',
        date: targetDate,
        time: reminder.time || '09:00',
        durationMins: Number(reminder.durationMins) || 45,
        location: reminder.location || '',
        description: reminder.description || '',
        color: deriveReminderColor(reminder.color),
        repeat: reminder.repeat || 'none',
        repeatCount: Number(reminder.repeatCount) || 1,
      }),
    );
    const normalized = toDateFromKey(targetDate);
    setSelectedDate(normalized);
    setCurrentDate(normalized);
    if (options.closeListModal) {
      setListModalOpen(false);
    }
    setFormOpen(true);
  };

  const handleOpenAddLabelForm = () => {
    setShowAddLabelForm(true);
    setShowEditLabelForm(false);
    setLabelCreationError('');
    setLabelEditError('');
    setEditingLabelKey('');
  };

  const loadLabelIntoEditDraft = useCallback(
    (key) => {
      if (!key) {
        return false;
      }
      const meta = labelTokens[key];
      if (!meta) {
        return false;
      }
      setEditingLabelKey(key);
      setEditLabelDraft({
        name: meta.label || '',
        color: meta.dotColor || '#4f46e5',
      });
      return true;
    },
    [labelTokens],
  );

  const handleOpenEditLabelForm = () => {
    if (!hasCustomLabels) {
      setLabelEditError('Create a label first, then edit it here.');
      return;
    }
    const keyToLoad = editingLabelKey && labelTokens[editingLabelKey] ? editingLabelKey : customLabelKeys[0];
    const loaded = loadLabelIntoEditDraft(keyToLoad);
    if (!loaded) {
      setLabelEditError('Unable to load label data.');
      return;
    }
    setShowAddLabelForm(false);
    setShowEditLabelForm(true);
    setLabelCreationError('');
    setLabelEditError('');
  };

  const handleEditLabelSelection = (event) => {
    const { value } = event.target;
    if (!value) {
      setEditingLabelKey('');
      return;
    }
    const loaded = loadLabelIntoEditDraft(value);
    if (!loaded) {
      setLabelEditError('Unable to load label data.');
    } else if (labelEditError) {
      setLabelEditError('');
    }
  };

  const handleEditLabelDraftChange = (event) => {
    const { name, value } = event.target;
    let nextValue = value;
    if (name === 'color') {
      const trimmed = value.trim();
      if (!trimmed) {
        nextValue = '';
      } else {
        const core = trimmed.replace(/^#+/, '');
        nextValue = `#${core}`;
      }
    }
    setEditLabelDraft((prev) => ({ ...prev, [name]: nextValue }));
    if (labelEditError) {
      setLabelEditError('');
    }
  };

  const handleCancelEditLabel = () => {
    setShowEditLabelForm(false);
    setLabelEditError('');
    setEditingLabelKey('');
  };

  const handleUpdateLabel = async (event) => {
    event.preventDefault();
    if (labelEditSaving) {
      return;
    }
    if (!editingLabelKey) {
      setLabelEditError('Select a label to edit.');
      return;
    }
    const labelName = editLabelDraft.name.trim();
    if (!labelName) {
      setLabelEditError('Give the label a name.');
      return;
    }
    const normalizedHex = normalizeHexColor(editLabelDraft.color);
    if (!normalizedHex) {
      setLabelEditError('Enter a valid hex color (e.g. #937666).');
      return;
    }
    const nextSlug = slugifyLabel(labelName);
    if (!nextSlug) {
      setLabelEditError('Name must include letters or numbers.');
      return;
    }
    if (nextSlug !== editingLabelKey && labelTokens[nextSlug]) {
      setLabelEditError('Label already exists.');
      return;
    }

    try {
      setLabelEditSaving(true);
      const updated = await reminderService.updateLabel(editingLabelKey, {
        name: labelName,
        colorHex: normalizedHex,
      });
      if (!updated) {
        setLabelEditError('Failed to update label. Please try again.');
        return;
      }
      const resolvedSlug = updated.slug || nextSlug;
      const nextToken = buildTokenFromApiLabel(updated);
      const previousKey = editingLabelKey;
      setLabelTokens((prev) => {
        const next = { ...prev };
        if (previousKey !== 'default') {
          delete next[previousKey];
        }
        next[resolvedSlug] = nextToken;
        return next;
      });
      setReminders((prev) =>
        prev.map((item) => (item.color === previousKey ? { ...item, color: resolvedSlug } : item)),
      );
      setLabelFilter((prev) => (prev === previousKey ? resolvedSlug : prev));
      setNewReminder((prev) => (prev.color === previousKey ? { ...prev, color: resolvedSlug } : prev));
      setLabelEditError('');
      setShowEditLabelForm(false);
      setEditingLabelKey('');
    } catch (err) {
      const serverErrors = err?.response?.data?.errors;
      const serverMessage = err?.response?.data?.message;
      if (serverErrors?.name?.length) {
        setLabelEditError(serverErrors.name[0]);
      } else if (serverErrors?.colorHex?.length) {
        setLabelEditError(serverErrors.colorHex[0]);
      } else {
        setLabelEditError(serverMessage || 'Failed to update label. Please try again.');
      }
    } finally {
      setLabelEditSaving(false);
    }
  };

  const handleCopyLocation = async (reminderId, location) => {
    if (!isLocationLink(location)) {
      return;
    }
    const trimmed = location.trim();
    try {
      if (typeof navigator !== 'undefined' && navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(trimmed);
      } else if (typeof document !== 'undefined') {
        const textArea = document.createElement('textarea');
        textArea.value = trimmed;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedLocationId(String(reminderId));
      if (copyResetRef.current) {
        clearTimeout(copyResetRef.current);
      }
      copyResetRef.current = setTimeout(() => {
        setCopiedLocationId(null);
        copyResetRef.current = null;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy reminder location', err);
    }
  };

  const handleToggleTodoStatus = async (todo) => {
    if (!todo?.id) {
      return;
    }
    const idKey = String(todo.id);
    if (todoUpdatingIds.includes(idKey)) {
      return;
    }
    const nextStatus = todo.status === 'completed' ? 'pending' : 'completed';
    setTodoActionError('');
    setTodoUpdatingIds((prev) => [...prev, idKey]);
    try {
      const updated = await todoService.updateStatus(todo.id, nextStatus);
      if (updated) {
        setTodos((prev) => prev.map((item) => (item.id === todo.id ? updated : item)));
      }
    } catch (err) {
      const serverMessage =
        err?.response?.data?.errors?.status?.[0] || err?.response?.data?.message || 'Failed to update task status.';
      setTodoActionError(serverMessage);
    } finally {
      setTodoUpdatingIds((prev) => prev.filter((value) => value !== idKey));
    }
  };

  const handleDeleteTodo = async (todo) => {
    const todoId = typeof todo === 'object' ? todo?.id : todo;
    if (!todoId) {
      return false;
    }
    const idKey = String(todoId);
    if (todoDeletingIds.includes(idKey)) {
      return false;
    }

    setTodoActionError('');
    setTodoDeletingIds((prev) => [...prev, idKey]);
    try {
      await todoService.deleteTodo(todoId);
      setTodos((prev) => prev.filter((item) => item.id !== todoId));
      return true;
    } catch (err) {
      const serverMessage = err?.response?.data?.message || 'Failed to delete task.';
      setTodoActionError(serverMessage);
      return false;
    } finally {
      setTodoDeletingIds((prev) => prev.filter((value) => value !== idKey));
    }
  };

  const openTodoDeleteConfirmation = (todo) => {
    if (!todo?.id) {
      return;
    }
    setTodoActionError('');
    setPendingDeleteTodo(todo);
  };

  const closeTodoDeleteConfirmation = () => {
    setPendingDeleteTodo(null);
  };

  const handleConfirmTodoDelete = async () => {
    if (!pendingDeleteTodo?.id) {
      return;
    }
    const success = await handleDeleteTodo(pendingDeleteTodo);
    if (success) {
      setPendingDeleteTodo(null);
    }
  };

  const handleOpenCreateTodoModal = () => {
    setTodoFormError('');
    setEditingTodoId(null);
    setNewTodoForm(
      buildTodoDraft({
        due_date: toDateKey(selectedDate),
        status: 'pending',
      }),
    );
    setShowAddTodoModal(true);
  };

  const handleEditTodo = (todo, options = {}) => {
    if (!todo?.id) {
      return;
    }
    setTodoFormError('');
    setEditingTodoId(todo.id);
    setNewTodoForm(
      buildTodoDraft({
        task_name: todo.task_name || '',
        description: todo.description || '',
        due_date: todo.due_date || toDateKey(selectedDate),
        status: todo.status || 'pending',
      }),
    );
    if (options.closeListModal) {
      setListModalOpen(false);
    }
    setShowAddTodoModal(true);
  };

  const closeAddTodoModal = () => {
    setShowAddTodoModal(false);
    setTodoFormError('');
    setEditingTodoId(null);
  };

  const handleNewTodoFieldChange = (event) => {
    const { name, value } = event.target;
    setNewTodoForm((prev) => ({ ...prev, [name]: value }));
    if (todoFormError) {
      setTodoFormError('');
    }
  };

  const handleCreateTodo = async (event) => {
    event.preventDefault();
    if (todoSaving) {
      return;
    }
    const taskName = newTodoForm.task_name.trim();
    if (!taskName) {
      setTodoFormError('Task name is required.');
      return;
    }
    const description = typeof newTodoForm.description === 'string' ? newTodoForm.description.trim() : '';
    const dueDate = typeof newTodoForm.due_date === 'string' ? newTodoForm.due_date.trim() : '';
    const payload = {
      task_name: taskName,
      description: description || null,
      due_date: dueDate || null,
      status: newTodoForm.status === 'completed' ? 'completed' : 'pending',
    };

    try {
      setTodoSaving(true);
      const response = editingTodoId
        ? await todoService.updateTodo(editingTodoId, payload)
        : await todoService.createTodo(payload);
      if (!response) {
        setTodoFormError('Failed to save task.');
        return;
      }
      setTodos((prev) => {
        if (editingTodoId) {
          return prev.map((item) => (String(item.id) === String(response.id) ? response : item));
        }
        return [response, ...prev];
      });
      setTodoActionError('');
      setShowAddTodoModal(false);
      setTodoFormError('');
      setEditingTodoId(null);
      setNewTodoForm(
        buildTodoDraft({
          due_date: toDateKey(selectedDate),
          status: 'pending',
        }),
      );
    } catch (err) {
      const errors = err?.response?.data?.errors;
      const message = err?.response?.data?.message;
      if (errors?.task_name?.length) {
        setTodoFormError(errors.task_name[0]);
      } else if (errors?.due_date?.length) {
        setTodoFormError(errors.due_date[0]);
      } else if (errors?.status?.length) {
        setTodoFormError(errors.status[0]);
      } else {
        setTodoFormError(message || 'Failed to create task.');
      }
    } finally {
      setTodoSaving(false);
    }
  };

  const handleCreateLabel = async (event) => {
    event.preventDefault();
    if (labelSaving) {
      return;
    }
    const labelName = newLabelDraft.name.trim();
    if (!labelName) {
      setLabelCreationError('Give the label a name.');
      return;
    }
    const key = slugifyLabel(labelName);
    if (!key) {
      setLabelCreationError('Name must include letters or numbers.');
      return;
    }
    if (labelTokens[key]) {
      setLabelCreationError('Label already exists.');
      return;
    }
    const normalizedHex = normalizeHexColor(newLabelDraft.color);
    if (!normalizedHex) {
      setLabelCreationError('Enter a valid hex color (e.g. #937666).');
      return;
    }

    try {
      setLabelSaving(true);
      const created = await reminderService.createLabel({ name: labelName, colorHex: normalizedHex });
      if (!created) {
        setLabelCreationError('Failed to create label. Please try again.');
        return;
      }
      const slug = created.slug || key;
      const nextToken = buildTokenFromApiLabel(created);
      setLabelTokens((prev) => ({ ...prev, [slug]: nextToken }));
      setNewReminder((prev) => ({ ...prev, color: slug }));
      setNewLabelDraft({ name: '', color: '#4f46e5' });
      setShowAddLabelForm(false);
      setLabelCreationError('');
      setLabelEditError('');
    } catch (err) {
      const serverErrors = err?.response?.data?.errors;
      const serverMessage = err?.response?.data?.message;
      if (serverErrors?.name?.length) {
        setLabelCreationError(serverErrors.name[0]);
      } else if (serverErrors?.colorHex?.length) {
        setLabelCreationError(serverErrors.colorHex[0]);
      } else {
        setLabelCreationError(serverMessage || 'Failed to create label. Please try again.');
      }
    } finally {
      setLabelSaving(false);
    }
  };

  const handleAddReminder = async (event) => {
    event.preventDefault();
    if (reminderSaving) {
      return;
    }
    const title = newReminder.title.trim();
    if (!title) {
      setFormError('Title is required.');
      return;
    }
    setFormError('');
    const repeatFrequency = newReminder.repeat || 'none';
    const normalizedRepeatCount = repeatFrequency === 'none'
      ? 1
      : Math.max(1, Number(newReminder.repeatCount) || 1);
    const payload = {
      title,
      date: newReminder.date,
      time: newReminder.time || null,
      durationMins: Number(newReminder.durationMins) || 30,
      location: newReminder.location.trim() || null,
      description: newReminder.description.trim() || null,
      repeat: repeatFrequency,
      repeatCount: normalizedRepeatCount,
      labelSlug: newReminder.color,
    };
    const failureMessage = editingReminderId ? 'Failed to update reminder.' : 'Failed to save reminder.';

    try {
      setReminderSaving(true);
      const response = editingReminderId
        ? await reminderService.updateReminder(editingReminderId, payload)
        : await reminderService.createReminder(payload);
      if (!response) {
        setFormError(failureMessage);
        return;
      }
      const normalized = normalizeReminderFromApi(response);
      setReminders((prev) => {
        const nextList = editingReminderId
          ? prev.map((item) => (String(item.id) === String(normalized.id) ? normalized : item))
          : [...prev, normalized];
        return sortByDateTime(nextList);
      });
      await refreshReminders();
      const selected = toDateFromKey(normalized.date);
      setSelectedDate(selected);
      setCurrentDate(selected);
      setNewReminder((prev) => ({
        ...prev,
        title: '',
        description: '',
        repeat: 'none',
        repeatCount: 1,
        date: toDateKey(selected),
        color: deriveReminderColor(normalized.color || prev.color),
      }));
      setEditingReminderId(null);
      setFormOpen(false);
    } catch (err) {
      const serverErrors = err?.response?.data?.errors;
      const serverMessage = err?.response?.data?.message;
      if (serverErrors?.title?.length) {
        setFormError(serverErrors.title[0]);
      } else if (serverErrors?.date?.length) {
        setFormError(serverErrors.date[0]);
      } else if (serverErrors?.labelSlug?.length) {
        setFormError(serverErrors.labelSlug[0]);
      } else {
        setFormError(serverMessage || failureMessage);
      }
    } finally {
      setReminderSaving(false);
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    const normalizedId = String(reminderId);
    if (!normalizedId || deletingReminderIds.includes(normalizedId)) {
      return false;
    }
    setDeleteError('');
    setDeletingReminderIds((prev) => [...prev, normalizedId]);
    try {
      await reminderService.deleteReminder(reminderId);
      setReminders((prev) => prev.filter((reminder) => String(reminder.id) !== normalizedId));
      await refreshReminders();
      return true;
    } catch (err) {
      const serverMessage = err?.response?.data?.message;
      setDeleteError(serverMessage || 'Failed to delete reminder.');
      return false;
    } finally {
      setDeletingReminderIds((prev) => prev.filter((id) => id !== normalizedId));
    }
  };

  const openDeleteConfirmation = (reminder) => {
    if (!reminder) {
      return;
    }
    setDeleteError('');
    setPendingDeleteReminder(reminder);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteReminder) {
      return;
    }
    const success = await handleDeleteReminder(pendingDeleteReminder.id);
    if (success) {
      setPendingDeleteReminder(null);
    }
  };

  const handleCancelDelete = () => {
    setPendingDeleteReminder(null);
  };

  const closeFormModal = () => {
    setFormOpen(false);
    setEditingReminderId(null);
    setFormError('');
  };
  const closeListModal = () => setListModalOpen(false);
  const handleOpenListModal = () => {
    setListModalOpen(true);
  };

  const gridColsClass = view === 'day' ? 'grid-cols-1' : 'grid-cols-7';
  const gridRowsClass =
    view === 'month'
      ? 'auto-rows-[150px] md:auto-rows-[170px]'
      : view === 'week'
        ? 'auto-rows-[220px]'
        : 'auto-rows-[320px]';
  const reminderFormSubtitle = isEditingReminder ? 'Quick edit' : 'Quick add';
  const reminderFormTitle = isEditingReminder ? 'Edit reminder' : 'New reminder';
  const reminderSubmitLabel = isEditingReminder ? 'Save changes' : 'Save reminder';
  const todoFormSubtitle = isEditingTodo ? 'Update task' : 'Quick task';
  const todoFormTitle = isEditingTodo ? 'Edit to-do' : 'New to-do';
  const todoSubmitLabel = isEditingTodo ? 'Save changes' : 'Save task';

  return (
    <DashboardLayout title="Remider">
      <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-6">
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-gray-100 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-3 py-1.5">
                <button
                  type="button"
                  onClick={() => handleNavigate(-1)}
                  className="rounded-full p-1 text-gray-500 transition hover:bg-gray-100"
                >
                  <MdChevronLeft />
                </button>
                <button
                  type="button"
                  onClick={goToToday}
                  className="flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1.5 text-sm font-semibold text-indigo-700"
                >
                  <MdCalendarToday />
                  Today
                </button>
                <button
                  type="button"
                  onClick={() => handleNavigate(1)}
                  className="rounded-full p-1 text-gray-500 transition hover:bg-gray-100"
                >
                  <MdChevronRight />
                </button>
              </div>
              <div className="text-left">
                <p className="text-2xl font-semibold text-gray-900">{calendarTitle}</p>
                <p className="text-sm text-gray-500">Centralised reminder wall for admin ops</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex items-center rounded-2xl border border-gray-200 bg-gray-50 p-1 text-sm font-semibold text-gray-500">
                {VIEW_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleViewChange(option.value)}
                    className={`rounded-xl px-3 py-1 transition ${
                      view === option.value ? 'bg-white text-indigo-600 shadow-sm' : ''}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={handleOpenCreateReminderForm}
                className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700"
              >
                <MdAdd />
                New reminder
              </button>
              <button
                type="button"
                onClick={handleOpenCreateTodoModal}
                className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-700"
              >
                <MdAdd />
                New to-do
              </button>
            </div>
          </div>
          <div className="flex flex-col lg:flex-row">
            <aside className="border-b border-gray-100 px-4 py-4 lg:w-72 lg:border-b-0 lg:border-r lg:px-5">
              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{formatMonthLabel(currentDate)}</p>
                    <p className="text-xs text-gray-500">{currentDate.getFullYear()}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleNavigate(-1)}
                      className="rounded-full p-1 text-gray-500 transition hover:bg-white"
                    >
                      <MdChevronLeft />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleNavigate(1)}
                      className="rounded-full p-1 text-gray-500 transition hover:bg-white"
                    >
                      <MdChevronRight />
                    </button>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-7 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {WEEKDAY_LABELS.map((day) => (
                    <span key={`mini-${day}`}>{day.charAt(0)}</span>
                  ))}
                </div>
                <div className="mt-1 grid grid-cols-7 gap-y-1 text-center text-sm">
                  {miniMonthDays.map((day) => {
                    const key = toDateKey(day);
                    const isToday = key === todayKey;
                    const selected = isSameDay(day, selectedDate);
                    const muted = !isSameMonth(day, currentDate);
                    return (
                      <button
                        key={`mini-cell-${key}`}
                        type="button"
                        onClick={() => handleSelectDay(day)}
                        className={`mx-auto flex h-8 w-8 items-center justify-center rounded-full border border-transparent text-xs font-semibold transition ${
                          selected
                            ? 'border-indigo-200 bg-indigo-600 text-white'
                            : isToday
                              ? 'text-indigo-600'
                              : ''} ${muted && !selected ? 'text-gray-400' : ''}`}
                      >
                        {day.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-5 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-900">Labels for {labelContextTitle}</p>
                  {viewRangeReminders.length > 0 && (
                    <span className="text-xs text-gray-500">
                      {labelFilter === 'all' ? 'Tap to filter' : 'Filtering calendar'}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {labelFilterOptions.map((option) => {
                    const active = labelFilter === option.value;
                    const inactiveStyle = { borderColor: '#e5e7eb', backgroundColor: '#ffffff', color: '#6b7280' };
                    const style = active ? option.chipStyle : inactiveStyle;
                    return (
                      <button
                        type="button"
                        key={`sidebar-label-filter-${option.value}`}
                        onClick={() => setLabelFilter(option.value)}
                        style={style}
                        className="inline-flex items-center gap-2 rounded-2xl border px-3 py-1.5 text-xs font-semibold transition"
                      >
                        <span className="h-2 w-2 rounded-full" style={option.dotStyle} />
                        {option.label}
                      </button>
                    );
                  })}
                </div>
                {labelFilter !== 'all' && (
                  <p className="text-xs font-semibold text-indigo-600">
                    Filtering calendar by {activeLabelMeta?.label || 'label'}
                  </p>
                )}
                {selectedDayReminders.length === 0 && (
                  <p className="rounded-2xl border border-dashed border-gray-200 bg-white p-4 text-xs text-gray-500">
                    No reminders for the selected date.
                  </p>
                )}
                <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-4 text-xs text-gray-600">
                  {showAddLabelForm ? (
                    <form className="space-y-3" onSubmit={handleCreateLabel}>
                      <div>
                        <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Label name</label>
                        <input
                          type="text"
                          name="name"
                          value={newLabelDraft.name}
                          onChange={handleLabelDraftChange}
                          className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-400"
                          placeholder="e.g. Outreach"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Color hex</label>
                        <input
                          type="text"
                          name="color"
                          value={newLabelDraft.color}
                          onChange={handleLabelDraftChange}
                          placeholder="#937666"
                          className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-400"
                        />
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                          <span>Preview:</span>
                          <span
                            className="inline-flex items-center gap-2 rounded-2xl border px-2 py-1"
                            style={draftPreviewToken ? getChipStyle(draftPreviewToken) : { borderColor: '#e5e7eb', backgroundColor: '#ffffff', color: '#9ca3af' }}
                          >
                            <span
                              className="h-2 w-2 rounded-full"
                              style={draftPreviewToken ? getDotStyle(draftPreviewToken) : { backgroundColor: '#d1d5db' }}
                            />
                            {draftPreviewToken ? (newLabelDraft.name || 'Label') : 'Invalid hex'}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {QUICK_COLOR_SWATCHES.map((hex) => (
                            <button
                              type="button"
                              key={`color-swatch-${hex}`}
                              onClick={() => setNewLabelDraft((prev) => ({ ...prev, color: hex }))}
                              className="h-7 w-7 rounded-full border border-gray-200"
                              style={{ backgroundColor: hex }}
                              aria-label={`Use color ${hex}`}
                            />
                          ))}
                        </div>
                      </div>
                      {labelCreationError && (
                        <p className="text-[11px] font-semibold text-rose-600">{labelCreationError}</p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="submit"
                          className="flex-1 rounded-2xl bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow hover:bg-indigo-700"
                        >
                          Save label
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowAddLabelForm(false);
                            setLabelCreationError('');
                            setNewLabelDraft({ name: '', color: '#4f46e5' });
                          }}
                          className="rounded-2xl border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : showEditLabelForm ? (
                    <form className="space-y-3" onSubmit={handleUpdateLabel}>
                      <div>
                        <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Pick a label</label>
                        <select
                          value={editingLabelKey}
                          onChange={handleEditLabelSelection}
                          className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-400"
                        >
                          <option value="">Select label</option>
                          {customLabelKeys.map((key) => {
                            const meta = resolveLabelToken(key);
                            return (
                              <option key={`edit-label-${key}`} value={key}>
                                {meta.label}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div>
                        <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Label name</label>
                        <input
                          type="text"
                          name="name"
                          value={editLabelDraft.name}
                          onChange={handleEditLabelDraftChange}
                          className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-400"
                          placeholder="e.g. Outreach"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Color hex</label>
                        <input
                          type="text"
                          name="color"
                          value={editLabelDraft.color}
                          onChange={handleEditLabelDraftChange}
                          placeholder="#937666"
                          className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-400"
                        />
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                          <span>Preview:</span>
                          <span
                            className="inline-flex items-center gap-2 rounded-2xl border px-2 py-1"
                            style={editDraftPreviewToken ? getChipStyle(editDraftPreviewToken) : { borderColor: '#e5e7eb', backgroundColor: '#ffffff', color: '#9ca3af' }}
                          >
                            <span
                              className="h-2 w-2 rounded-full"
                              style={editDraftPreviewToken ? getDotStyle(editDraftPreviewToken) : { backgroundColor: '#d1d5db' }}
                            />
                            {editDraftPreviewToken ? (editLabelDraft.name || 'Label') : 'Invalid hex'}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {QUICK_COLOR_SWATCHES.map((hex) => (
                            <button
                              type="button"
                              key={`edit-color-swatch-${hex}`}
                              onClick={() => setEditLabelDraft((prev) => ({ ...prev, color: hex }))}
                              className="h-7 w-7 rounded-full border border-gray-200"
                              style={{ backgroundColor: hex }}
                              aria-label={`Use color ${hex}`}
                            />
                          ))}
                        </div>
                      </div>
                      {labelEditError && (
                        <p className="text-[11px] font-semibold text-rose-600">{labelEditError}</p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="submit"
                          disabled={labelEditSaving}
                          className="flex-1 rounded-2xl bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {labelEditSaving ? 'Saving...' : 'Save changes'}
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelEditLabel}
                          className="rounded-2xl border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Label toolkit</p>
                      <button
                        type="button"
                        onClick={handleOpenAddLabelForm}
                        className="w-full rounded-2xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-700 transition hover:bg-white"
                      >
                        + Add label
                      </button>
                      <button
                        type="button"
                        onClick={handleOpenEditLabelForm}
                        disabled={!hasCustomLabels}
                        className="flex w-full items-center justify-center gap-2 rounded-2xl border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-600 transition hover:border-gray-300 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <MdEdit />
                        Edit label
                      </button>
                      <p className="text-[11px] text-gray-500">Create quick tags to group reminders.</p>
                      {!hasCustomLabels && (
                        <p className="text-[11px] text-gray-400">Add a label before editing it.</p>
                      )}
                      {labelEditError && !showEditLabelForm && (
                        <p className="text-[11px] font-semibold text-rose-600">{labelEditError}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </aside>

            <main className="flex-1 border-gray-100 px-4 py-6 md:px-6">
              {view !== 'day' && (
                <div className={`mb-2 grid ${view === 'day' ? 'grid-cols-1' : 'grid-cols-7'} text-xs font-semibold uppercase tracking-wide text-gray-400`}>
                  {WEEKDAY_LABELS.map((day) => (
                    <span key={`head-${day}`} className="px-3">
                      {day}
                    </span>
                  ))}
                </div>
              )}
              <div className={`grid gap-2 ${gridColsClass} ${gridRowsClass}`}>
                {calendarDays.map((day) => {
                  const key = toDateKey(day);
                  const todaysCell = key === todayKey;
                  const isSelected = isSameDay(day, selectedDate);
                  const inactive = view === 'month' && !isSameMonth(day, currentDate);
                  const dayReminders = filteredRemindersByDate[key] || [];
                  const dayTodos = todosByDate[key] || [];
                  const combinedEntries = [
                    ...dayReminders.map((entry) => ({ kind: 'reminder', data: entry })),
                    ...dayTodos.map((entry) => ({ kind: 'todo', data: entry })),
                  ];
                  const visibleEntries = view === 'day' ? combinedEntries : combinedEntries.slice(0, 3);
                  const hiddenCount = combinedEntries.length - visibleEntries.length;
                  return (
                    <div
                      key={`calendar-cell-${key}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelectDay(day)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          handleSelectDay(day);
                        }
                      }}
                      className={`flex flex-col rounded-2xl border p-3 transition ${
                        isSelected
                          ? 'border-indigo-300 bg-indigo-50'
                          : todaysCell
                            ? 'border-indigo-200 bg-white'
                            : 'border-gray-100 bg-white'} ${inactive ? 'opacity-60' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-semibold ${isSelected ? 'text-indigo-700' : 'text-gray-900'}`}>
                          {day.getDate()}
                        </span>
                        {todaysCell && (
                          <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-500">Today</span>
                        )}
                      </div>
                      <div className="mt-3 space-y-2 overflow-hidden text-xs">
                        {visibleEntries.map((entry) => {
                          if (entry.kind === 'reminder') {
                            const item = entry.data;
                            const meta = resolveLabelToken(item.color);
                            return (
                              <button
                                type="button"
                                key={`cell-reminder-${key}-${item.id}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleSelectDay(toDateFromKey(item.date));
                                }}
                                style={getSurfaceStyle(meta)}
                                className="flex w-full items-center gap-2 rounded-xl border px-2 py-1 text-left text-xs font-semibold transition hover:bg-white"
                              >
                                <span className="h-2 w-2 rounded-full" style={getDotStyle(meta)} />
                                <span className="flex-1 truncate">{item.title}</span>
                                <span className="text-[10px] font-medium text-gray-500">{formatTimeLabel(item.time)}</span>
                              </button>
                            );
                          }
                          const todo = entry.data;
                          return (
                            <button
                              type="button"
                              key={`cell-todo-${key}-${todo.id}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleSelectDay(day);
                              }}
                              style={getSurfaceStyle(TODO_LABEL_TOKEN)}
                              className="flex w-full items-center gap-2 rounded-xl border px-2 py-1 text-left text-xs font-semibold transition hover:bg-white"
                            >
                              <span className="h-2 w-2 rounded-full" style={getDotStyle(TODO_LABEL_TOKEN)} />
                              <span className="flex-1 truncate">{todo.task_name}</span>
                              <span className="text-[10px] font-medium text-gray-500">
                                {todo.status === 'completed' ? 'Done' : 'To-do'}
                              </span>
                            </button>
                          );
                        })}
                        {hiddenCount > 0 && view !== 'day' && (
                          <span className="text-[11px] font-semibold text-indigo-600">+{hiddenCount} more</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </main>
          </div>
        </div>

        <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-gray-400">Focus day</p>
                <h2 className="text-2xl font-semibold text-gray-900">{formatFullDate(selectedDate)}</h2>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-500">
                  <MdEventAvailable />
                  {filteredSelectedReminders.length} reminders
                </div>
                <button
                  type="button"
                  onClick={handleOpenListModal}
                  className="rounded-full border border-gray-200 px-4 py-1.5 text-xs font-semibold text-gray-600 transition hover:border-gray-300 hover:text-gray-900"
                >
                  View in modal
                </button>
              </div>
            </div>
            {deleteError && (
              <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-2 text-sm text-rose-700">
                {deleteError}
              </div>
            )}
            <div className="mt-6 space-y-4">
              {selectedDayReminders.length === 0 && (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">
                  Nothing scheduled for this day. Use the quick add form to block time.
                </div>
              )}
              {selectedDayReminders.length > 0 && filteredSelectedReminders.length === 0 && (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">
                  No reminders match the selected label for this day.
                </div>
              )}
              {filteredSelectedReminders.map((item) => {
                const meta = resolveLabelToken(item.color);
                const reminderKey = String(item.id);
                const deleting = deletingReminderIds.includes(reminderKey);
                const locationIsLink = isLocationLink(item.location);
                return (
                  <div
                    key={`detail-${item.id}`}
                    className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-3 shadow-sm"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={getDotStyle(meta)} />
                        <p className="text-base font-semibold text-gray-900">{item.title}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                          {meta.label}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleEditReminder(item)}
                          disabled={deleting}
                          className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600 transition hover:border-indigo-200 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <MdEdit />
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => openDeleteConfirmation(item)}
                          disabled={deleting}
                          className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600 transition hover:border-rose-200 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                          aria-label="Delete reminder"
                        >
                          <MdDeleteOutline />
                          {deleting ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                      <span className="inline-flex items-center gap-1">
                        <MdSchedule />
                        {formatRangeLabel(item.time, item.durationMins)}
                      </span>
                      {item.location && (
                        <span className="inline-flex items-center gap-1">
                          <MdLocationOn />
                          {locationIsLink ? (
                            <a
                              href={item.location}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="break-all text-indigo-600 hover:underline"
                            >
                              {item.location}
                            </a>
                          ) : (
                            item.location
                          )}
                        </span>
                      )}
                    </div>
                    {item.description && (
                      <p className="text-sm text-gray-600">{item.description}</p>
                    )}
                  </div>
                );
              })}
            </div>
        </section>

        <section className="mt-6 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-gray-400">To-do list</p>
              <h2 className="text-2xl font-semibold text-gray-900">Tasks for {formatFullDate(selectedDate)}</h2>
              <p className="text-sm text-gray-500">Pulled directly from the shared admin to-do table.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700">
                Pending
                <span className="rounded-full bg-white px-2 py-0.5 text-orange-600">{pendingTodoCount}</span>
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                Completed
                <span className="rounded-full bg-white px-2 py-0.5 text-emerald-600">{completedTodoCount}</span>
              </span>
              <a
                href="/admin/todo-list"
                className="inline-flex items-center rounded-full border border-gray-200 px-4 py-1.5 text-xs font-semibold text-gray-600 transition hover:border-gray-300 hover:text-gray-900"
              >
                Open full view
              </a>
            </div>
          </div>
          {todoError && (
            <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-2 text-sm text-rose-700">
              {todoError}
            </div>
          )}
          {todoActionError && !todoError && (
            <div className="mt-4 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-2 text-sm text-amber-800">
              {todoActionError}
            </div>
          )}
          <div className="mt-6">
            {todoLoading ? (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">
                Loading tasks...
              </div>
            ) : selectedDayTodos.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">
                No tasks scheduled for this date. Use the main To-do List screen to add one.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="min-w-full overflow-hidden rounded-2xl border border-gray-100">
                  <table className="min-w-full divide-y divide-gray-100 text-sm">
                    <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-4 py-3">Task</th>
                        <th className="px-4 py-3">Description</th>
                        <th className="px-4 py-3">Due</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {selectedDayTodos.map((todo) => {
                        const idKey = String(todo.id);
                        const statusPillClass =
                          todo.status === 'completed'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                            : 'bg-orange-50 text-orange-700 border border-orange-100';
                        const dueLabel = todo.due_date ? formatShortDate(toDateFromKey(todo.due_date)) : 'No due date';
                        const updating = todoUpdatingIds.includes(idKey);
                        const deleting = todoDeletingIds.includes(idKey);
                        return (
                          <tr key={`todo-row-${todo.id}`} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-semibold text-gray-900">{todo.task_name}</td>
                            <td className="px-4 py-3 text-gray-600">
                              {todo.description ? (
                                <span className="text-sm text-gray-600">{todo.description}</span>
                              ) : (
                                <span className="text-xs text-gray-400">No description</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{dueLabel}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusPillClass}`}>
                                {todo.status === 'completed' ? 'Completed' : 'Pending'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleEditTodo(todo)}
                                  disabled={updating || deleting}
                                  className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600 transition hover:border-emerald-200 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  <MdEdit />
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleToggleTodoStatus(todo)}
                                  disabled={updating || deleting}
                                  className="rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600 transition hover:border-indigo-200 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  {updating
                                    ? 'Updating...'
                                    : todo.status === 'completed'
                                      ? 'Mark pending'
                                      : 'Mark complete'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openTodoDeleteConfirmation(todo)}
                                  disabled={deleting || updating}
                                  className="rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600 transition hover:border-rose-200 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  {deleting ? 'Deleting...' : 'Delete'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
      {listModalOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4"
          onClick={closeListModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Reminders for</p>
                <h3 className="text-2xl font-semibold text-gray-900">{formatFullDate(selectedDate)}</h3>
                <p className="text-xs text-gray-500">
                  Showing {filteredSelectedReminders.length} of {selectedDayReminders.length} reminders
                </p>
              </div>
              <button
                type="button"
                onClick={closeListModal}
                className="rounded-full border border-gray-200 p-2 text-gray-500 hover:bg-gray-50"
                aria-label="Close reminder list"
              >
                <MdClose />
              </button>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {labelFilterOptions.map((option) => {
                const active = labelFilter === option.value;
                const inactiveStyle = { borderColor: '#e5e7eb', backgroundColor: '#ffffff', color: '#6b7280' };
                const style = active ? option.chipStyle : inactiveStyle;
                return (
                  <button
                    type="button"
                    key={`label-filter-${option.value}`}
                    onClick={() => setLabelFilter(option.value)}
                    style={style}
                    className="inline-flex items-center gap-2 rounded-2xl border px-3 py-1.5 text-xs font-semibold transition"
                  >
                    <span className="h-2 w-2 rounded-full" style={option.dotStyle} />
                    {option.label}
                  </button>
                );
              })}
            </div>
            {deleteError && (
              <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-2 text-sm text-rose-700">
                {deleteError}
              </div>
            )}
            <div className="mt-6 max-h-[60vh] space-y-4 overflow-y-auto pr-1">
              {filteredSelectedReminders.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">
                  No reminders match this filter for the selected day.
                </div>
              ) : (
                filteredSelectedReminders.map((item) => {
                  const meta = resolveLabelToken(item.color);
                  const reminderKey = String(item.id);
                  const deleting = deletingReminderIds.includes(reminderKey);
                  const locationIsLink = isLocationLink(item.location);
                  const copying = copiedLocationId === reminderKey;
                  return (
                    <div
                      key={`modal-detail-${item.id}`}
                      className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-3 shadow-sm"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full" style={getDotStyle(meta)} />
                          <p className="text-base font-semibold text-gray-900">{item.title}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">{meta.label}</span>
                          <button
                            type="button"
                            onClick={() => handleEditReminder(item, { closeListModal: true })}
                            disabled={deleting}
                            className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600 transition hover:border-indigo-200 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <MdEdit />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => openDeleteConfirmation(item)}
                            disabled={deleting}
                            className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600 transition hover:border-rose-200 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                            aria-label="Delete reminder"
                          >
                            <MdDeleteOutline />
                            {deleting ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                        <span className="inline-flex items-center gap-1">
                          <MdSchedule />
                          {formatRangeLabel(item.time, item.durationMins)}
                        </span>
                        {item.location && (
                          <span className="inline-flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center gap-1">
                              <MdLocationOn />
                              {locationIsLink ? (
                                <a
                                  href={item.location}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="break-all text-indigo-600 hover:underline"
                                >
                                  {item.location}
                                </a>
                              ) : (
                                item.location
                              )}
                            </span>
                            {locationIsLink && (
                              <button
                                type="button"
                                onClick={() => handleCopyLocation(item.id, item.location)}
                                className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600 transition hover:border-indigo-200 hover:text-indigo-600"
                              >
                                <MdContentCopy />
                                {copying ? 'Copied' : 'Copy link'}
                              </button>
                            )}
                          </span>
                        )}
                      </div>
                      {item.description && <p className="text-sm text-gray-600">{item.description}</p>}
                    </div>
                  );
                })
              )}
            </div>
            <div className="mt-8 border-t border-gray-100 pt-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">To-do list</p>
                  <h4 className="text-lg font-semibold text-gray-900">Tasks for {formatFullDate(selectedDate)}</h4>
                </div>
                <div className="inline-flex items-center gap-2 text-xs font-semibold text-gray-500">
                  <span className="rounded-full bg-orange-50 px-2 py-0.5 text-orange-700">Pending {pendingTodoCount}</span>
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">Completed {completedTodoCount}</span>
                </div>
              </div>
              {todoActionError && (
                <div className="mt-4 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-2 text-sm text-amber-800">
                  {todoActionError}
                </div>
              )}
              <div className="mt-4 space-y-3">
                {todoLoading ? (
                  <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-500">
                    Loading tasks...
                  </div>
                ) : selectedDayTodos.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-500">
                    No tasks for this date.
                  </div>
                ) : (
                  selectedDayTodos.map((todo) => {
                    const idKey = String(todo.id);
                    const updating = todoUpdatingIds.includes(idKey);
                    const deleting = todoDeletingIds.includes(idKey);
                    const pillClass =
                      todo.status === 'completed'
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                        : 'bg-orange-50 text-orange-700 border border-orange-100';
                    const dueLabel = todo.due_date ? formatShortDate(toDateFromKey(todo.due_date)) : 'No due date';
                    return (
                      <div
                        key={`modal-todo-${todo.id}`}
                        className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-3 shadow-sm"
                      >
                        <div className="flex items-center justify-between gap-4">
                          <p className="text-base font-semibold text-gray-900">{todo.task_name}</p>
                          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${pillClass}`}>
                            {todo.status === 'completed' ? 'Completed' : 'Pending'}
                          </span>
                        </div>
                        {todo.description && <p className="text-sm text-gray-600">{todo.description}</p>}
                        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
                          <span className="inline-flex items-center gap-1">
                            <MdSchedule />
                            {dueLabel}
                          </span>
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleEditTodo(todo, { closeListModal: true })}
                              disabled={updating || deleting}
                              className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600 transition hover:border-emerald-200 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <MdEdit />
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleToggleTodoStatus(todo)}
                              disabled={updating || deleting}
                              className="rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600 transition hover:border-indigo-200 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {updating
                                ? 'Updating...'
                                : todo.status === 'completed'
                                  ? 'Mark pending'
                                  : 'Mark complete'}
                            </button>
                            <button
                              type="button"
                              onClick={() => openTodoDeleteConfirmation(todo)}
                              disabled={deleting || updating}
                              className="rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600 transition hover:border-rose-200 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {formOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={closeFormModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="rounded-full bg-indigo-100 p-2 text-indigo-600">
                  {isEditingReminder ? <MdEdit /> : <MdAdd />}
                </div>
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-gray-400">{reminderFormSubtitle}</p>
                  <h3 className="text-xl font-semibold text-gray-900">{reminderFormTitle}</h3>
                </div>
              </div>
              <button
                type="button"
                onClick={closeFormModal}
                className="rounded-full border border-gray-200 p-2 text-gray-500 hover:bg-gray-50"
                aria-label="Close reminder form"
              >
                <MdClose />
              </button>
            </div>
            <form className="mt-6 space-y-4" onSubmit={handleAddReminder}>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Title</label>
                <input
                  type="text"
                  name="title"
                  value={newReminder.title}
                  onChange={handleFormChange}
                  placeholder="e.g. Monthly fees follow-up"
                  className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400"
                  autoFocus
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Date</label>
                  <input
                    type="date"
                    name="date"
                    value={newReminder.date}
                    onChange={handleFormChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Start time</label>
                  <input
                    type="time"
                    name="time"
                    value={newReminder.time}
                    onChange={handleFormChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Duration (mins)</label>
                <input
                  type="number"
                  name="durationMins"
                  min="15"
                  step="15"
                  value={newReminder.durationMins}
                  onChange={handleFormChange}
                  className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Repeat</label>
                  <select
                    name="repeat"
                    value={newReminder.repeat}
                    onChange={handleFormChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400"
                  >
                    {REPEAT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {REPEAT_OPTIONS.filter((option) => option.value !== 'none').map((option) => {
                      const active = newReminder.repeat === option.value;
                      return (
                        <button
                          type="button"
                          key={`quick-repeat-${option.value}`}
                          onClick={() => setNewReminder((prev) => ({ ...prev, repeat: option.value }))}
                          className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                            active
                              ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {newReminder.repeat !== 'none' && (
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Repeat count (occurrences)
                    </label>
                    <input
                      type="number"
                      name="repeatCount"
                      min="1"
                      max="36"
                      value={newReminder.repeatCount}
                      onChange={handleFormChange}
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400"
                    />
                    <p className="mt-1 text-[11px] text-gray-500">Includes the first reminder.</p>
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Location / link</label>
                <input
                  type="text"
                  name="location"
                  value={newReminder.location}
                  onChange={handleFormChange}
                  placeholder="Zoom / Boardroom / Portal"
                  className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Notes</label>
                <textarea
                  name="description"
                  value={newReminder.description}
                  onChange={handleFormChange}
                  rows={3}
                  placeholder="Context or handover notes"
                  className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Label</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(labelTokens)
                    .filter(([key]) => key !== 'default')
                    .map(([key, meta]) => {
                      const selected = newReminder.color === key;
                      const inactiveStyle = { borderColor: '#e5e7eb', backgroundColor: '#ffffff', color: '#6b7280' };
                      const style = selected ? getChipStyle(meta) : inactiveStyle;
                      return (
                        <button
                          type="button"
                          key={`color-${key}`}
                          onClick={() => setNewReminder((prev) => ({ ...prev, color: key }))}
                          style={style}
                          className="inline-flex items-center gap-2 rounded-2xl border px-3 py-1 text-xs font-semibold transition"
                        >
                          <span className="h-2 w-2 rounded-full" style={getDotStyle(meta)} />
                          {meta.label}
                        </button>
                      );
                    })}
                </div>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeFormModal}
                  className="rounded-2xl border border-gray-200 px-6 py-3 text-sm font-semibold text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={reminderSaving}
                  className="rounded-2xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {reminderSaving ? 'Saving...' : reminderSubmitLabel}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showAddTodoModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={closeAddTodoModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="rounded-full bg-emerald-100 p-2 text-emerald-600">
                  {isEditingTodo ? <MdEdit /> : <MdAdd />}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{todoFormSubtitle}</p>
                  <h3 className="text-xl font-semibold text-gray-900">{todoFormTitle}</h3>
                </div>
              </div>
              <button
                type="button"
                onClick={closeAddTodoModal}
                className="rounded-full border border-gray-200 p-2 text-gray-500 hover:bg-gray-50"
                aria-label="Close to-do form"
              >
                <MdClose />
              </button>
            </div>
            <form className="mt-6 space-y-4" onSubmit={handleCreateTodo}>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Task name</label>
                <input
                  type="text"
                  name="task_name"
                  value={newTodoForm.task_name}
                  onChange={handleNewTodoFieldChange}
                  placeholder="e.g. Follow up with finance"
                  className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-emerald-400"
                  autoFocus
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Due date</label>
                  <input
                    type="date"
                    name="due_date"
                    value={newTodoForm.due_date || ''}
                    onChange={handleNewTodoFieldChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-emerald-400"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Status</label>
                  <select
                    name="status"
                    value={newTodoForm.status}
                    onChange={handleNewTodoFieldChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-emerald-400"
                  >
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Description</label>
                <textarea
                  name="description"
                  rows={3}
                  value={newTodoForm.description || ''}
                  onChange={handleNewTodoFieldChange}
                  placeholder="Context, owners, or links"
                  className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-emerald-400"
                />
              </div>
              {todoFormError && (
                <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-2 text-sm text-rose-700">
                  {todoFormError}
                </div>
              )}
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeAddTodoModal}
                  className="rounded-2xl border border-gray-200 px-6 py-3 text-sm font-semibold text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={todoSaving}
                  className="rounded-2xl bg-emerald-600 px-6 py-3 text-sm font-semibold text-white shadow transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {todoSaving ? 'Saving...' : todoSubmitLabel}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {pendingDeleteReminder && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={handleCancelDelete}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-gray-400">Confirm delete</p>
                <h3 className="text-xl font-semibold text-gray-900">Remove this reminder?</h3>
              </div>
              <button
                type="button"
                onClick={handleCancelDelete}
                className="rounded-full border border-gray-200 p-2 text-gray-500 hover:bg-gray-50"
                aria-label="Close delete confirmation"
              >
                <MdClose />
              </button>
            </div>
            <p className="mt-4 text-sm text-gray-600">
              "{pendingDeleteReminder.title}" will be permanently deleted. This action cannot be undone.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleCancelDelete}
                disabled={pendingDeleteInFlight}
                className="rounded-2xl border border-gray-200 px-6 py-3 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Keep reminder
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={pendingDeleteInFlight}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-rose-600 px-6 py-3 text-sm font-semibold text-white shadow transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <MdDeleteOutline />
                {pendingDeleteInFlight ? 'Deleting...' : 'Delete reminder'}
              </button>
            </div>
          </div>
        </div>
      )}
      {pendingDeleteTodo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={closeTodoDeleteConfirmation}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-gray-400">Confirm removal</p>
                <h3 className="text-xl font-semibold text-gray-900">Remove this task?</h3>
              </div>
              <button
                type="button"
                onClick={closeTodoDeleteConfirmation}
                className="rounded-full border border-gray-200 p-2 text-gray-500 hover:bg-gray-50"
                aria-label="Close delete confirmation"
              >
                <MdClose />
              </button>
            </div>
            <p className="mt-4 text-sm text-gray-600">
              "{pendingDeleteTodo.task_name}" will disappear from the shared to-do list. You can always add it again later.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeTodoDeleteConfirmation}
                disabled={pendingTodoDeleteInFlight}
                className="rounded-2xl border border-gray-200 px-6 py-3 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Keep task
              </button>
              <button
                type="button"
                onClick={handleConfirmTodoDelete}
                disabled={pendingTodoDeleteInFlight}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-rose-600 px-6 py-3 text-sm font-semibold text-white shadow transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <MdDeleteOutline />
                {pendingTodoDeleteInFlight ? 'Removing...' : 'Remove task'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
